import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Store } from '@ngxs/store';
import { of, switchMap } from 'rxjs';
import { AuthService } from '../../../../services/account/auth.service';
import { AccountCentreService } from '../../../../account-centre/account-centre.service';
import { UserStateModel } from '../../../../states/user/user-state-model';
import { UserResponse } from '../../../../models/account/userResponse';
import { UserSelectors } from '../../../../states/user/user-selector';
import { UpdateProfileCoverImage } from '../../../../states/user/user-action';
import { ImageCropUploadComponent } from '../../../../utility/image-crop-upload/image-crop-upload.component';

@Component({
  selector: 'app-profile-photo-card',
  standalone: true,
  imports: [CommonModule, FormsModule, ImageCropUploadComponent],
  templateUrl: './profile-photo-card.component.html',
  styleUrl: './profile-photo-card.component.scss'
})
export class ProfilePhotoCardComponent implements OnInit {

  user!: UserStateModel;
  userDetails: UserResponse | null = null;
  profilePhoto: File | Blob | undefined;
  profileImage = '../../../assets/images/avatar1.jpg';
  isUploading = false;

  constructor(
    private messageService: MessageService,
    private accountService: AccountCentreService,
    private authService: AuthService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.loadUserData();
  }

  private loadUserData() {
    this.store.select(UserSelectors.getUser).pipe(
      switchMap((user) => {
        if (!user || !user.id) {
          return of(null);
        }
        this.user = user;
        return this.authService.getUserByid(user.id);
      })
    ).subscribe({
      next: (response) => {
        if (response != null) {
          this.userDetails = response;
          this.profileImage = response.imageUrl ?? '../../../assets/images/avatar1.jpg';
        }
      }
    });
  }

  onProfileCropped(base64: string) {
    this.profileImage = base64;
    this.profilePhoto = this.dataURLtoFile(base64, 'profile.png');
    this.onSubmit(); // automatically submit when cropped
  }

  dataURLtoFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  }

  onSubmit() {
    if (!this.profilePhoto) return;
    this.isUploading = true;
    this.accountService.updatePhoto({
      profilePhoto: this.profilePhoto instanceof File ? this.profilePhoto : null,
      coverPhoto: null // Dropped cover photo
    }).subscribe({
      next: (response) => {
        this.isUploading = false;
        this.store.dispatch(new UpdateProfileCoverImage({
          profileImageUrl: response.profileImageUrl ?? undefined,
          profileImageUrlFormat: response.profileImageFormat ?? undefined
        }));
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Profile photo updated successfully' });
      },
      error: (err) => {
        this.isUploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update photo' });
      }
    });
  }
}
